import {
  AbstractControl,
  AsyncValidatorFn,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { BaseHttpService } from '@core/http/base-http.service';
import { Observable, of, timer } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { CustomAttributeValue } from '../interfaces/attribute-description.interface';
import { FormUtils } from './form-utils';

export const CHECK_DUPLICATE_NAME_DEBOUNCE_MS = 400;

export class TextValidator {
  static notEmptyOrWhitespace(
    control: AbstractControl
  ): ValidationErrors | null {
    if (control.value && typeof control.value === 'string') {
      if (control.value.trim() === '') {
        return { notEmptyOrWhitespace: true };
      }
    }

    return null;
  }

  static cannotContainWhitespace(
    control: AbstractControl
  ): ValidationErrors | null {
    const fieldValue = (control.value as string).trim();
    if (fieldValue?.includes(' ')) {
      return { cannotContainWhitespace: true };
    }

    return null;
  }

  static cannotContainManySpaces(
    control: AbstractControl
  ): ValidationErrors | null {
    const fieldValue = (control.value as string).trim();
    if (fieldValue?.includes('  ')) {
      return { cannotContainManySpaces: true };
    }

    return null;
  }

  static uniqueOrgName(control: AbstractControl): ValidationErrors | null {
    const fieldValue = (control.value as string).trim();
    if (fieldValue?.includes('a')) {
      return { uniqueOrgName: true };
    }

    return null;
  }
}

export class EmailValidator {
  static pattern(regex: string): ValidatorFn {
    const emailRegex = EmailValidator.compileAnchoredRegex(regex);

    if (!emailRegex) {
      return Validators.email;
    }

    return (control: AbstractControl): ValidationErrors | null => {
      if (
        control.value === null ||
        control.value === undefined ||
        control.value === ''
      ) {
        return null;
      }

      return emailRegex.test(control.value as string) ? null : { email: true };
    };
  }

  // The backend pattern is authored for .NET; grammar differences can make it
  // an invalid JS regex, so a compile failure must not break the form.
  private static compileAnchoredRegex(pattern: string): RegExp | null {
    try {
      return new RegExp(EmailValidator.wrapWithAnchors(pattern));
    } catch {
      return null;
    }
  }

  // Wrapping in a non-capturing group keeps top-level alternations ("a|b")
  // anchored to the whole string instead of parsing as "^a" or "b$".
  private static wrapWithAnchors(pattern: string): string {
    let core = pattern;

    if (core.startsWith('^')) {
      core = core.substring(1);
    }

    if (core.endsWith('$') && !core.endsWith(String.raw`\$`)) {
      core = core.substring(0, core.length - 1);
    }

    return `^(?:${core})$`;
  }
}

export class SingleSelectionValidator {
  static required(control: AbstractControl): ValidationErrors | null {
    const fieldValue: CustomAttributeValue = control.value;
    if (
      fieldValue &&
      (fieldValue.codeId === 'DefaultValue' || !fieldValue.codeId)
    ) {
      return { required: true };
    }
    return null;
  }
}

export class DateValidators {
  static compareTwoDate(
    dateField1: string,
    isLessThan: boolean,
    dateField2: string,
    validatorErrorName: string
  ): ValidatorFn {
    return (control: AbstractControl): Record<string, boolean> | null => {
      const mainControl = control.get(dateField1);

      const date1 = mainControl?.value;
      const date2 = control.get(dateField2)?.value;
      const validatorField = {};
      validatorField[validatorErrorName] = true;

      if (date1 !== null && date2 !== null && isLessThan !== undefined) {
        if (isLessThan && date1 > date2) {
          mainControl.setErrors(validatorField);
          return validatorField;
        }
        if (!isLessThan && date2 > date1) {
          mainControl.setErrors(validatorField);
          return validatorField;
        }
      }
      FormUtils.removeError(mainControl, validatorErrorName);
      return null;
    };
  }
}

export class UniqueNameValidator {
  static validateDuplicateNameFromApi<T>(
    httpService: BaseHttpService<T>,
    initialValue: string,
    ignoredNames: string[] | (() => string[]) = []
  ): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const fieldValue = (control.value as string)?.trim();
      const ignored =
        typeof ignoredNames === 'function' ? ignoredNames() : ignoredNames;

      if (
        initialValue === fieldValue ||
        (fieldValue && ignored.includes(fieldValue))
      ) {
        return of(null);
      }

      if (!fieldValue) {
        return of(null);
      }

      return timer(CHECK_DUPLICATE_NAME_DEBOUNCE_MS).pipe(
        switchMap(() => httpService.checkDuplicateName(fieldValue)),
        map(() => null),
        catchError(error =>
          of({
            nameDuplicated: {
              message: error.error?.title || 'Duplicate name',
            },
          })
        )
      );
    };
  }
}

export class UniqueSubdomainValidator {
  static validateSubdomainNotTaken<T>(
    getHttpService: () => BaseHttpService<T> | null,
    initialValue: string,
    errorMessage = 'Subdomain already taken'
  ): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const fieldValue = (control.value as string)?.trim();

      if (!fieldValue || fieldValue === initialValue?.trim()) {
        return of(null);
      }

      const httpService = getHttpService();

      if (!httpService) {
        return of(null);
      }

      return timer(CHECK_DUPLICATE_NAME_DEBOUNCE_MS).pipe(
        switchMap(() => httpService.checkSubdomainExists(fieldValue)),
        map(exists =>
          exists ? { subdomainDuplicated: { message: errorMessage } } : null
        ),
        catchError(() => of(null))
      );
    };
  }
}
